import React, { useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import QuotationItemSelect from "./QuotationItemSelect";
import QuotationSectionOneHeader from "./QuotationSectionOneHeader";
import { ServiceCostDiscountControl, ServiceCostLinearSummary } from "../service/ServiceCostSection";
import { cqItemModels, cqItemsForModel, cqPartSelectionState, localKey } from "../../lib/activeQuotation";
import { calculateQuotationLine } from "../../lib/quotationForm";
import { formatQuotationMoney } from "../../lib/quotationHistory";

const inputClass = "wms-input h-10 w-full min-w-0 text-sm";
const textareaClass = "wms-input min-h-24 w-full min-w-0 resize-y py-2 text-sm";

const Field = ({ label, children, className = "" }) => (
  <label className={`block min-w-0 text-xs font-medium ${className}`}>
    {label}
    {children}
  </label>
);

export default function CqQuotationSection({ mode, draft, setDraft, editable, items, totals, headerAction = null, headerHelper = "Create a quotation without linking to a Service Ticket." }) {
  const [serialInput, setSerialInput] = useState("");
  const [serialError, setSerialError] = useState("");
  const [discountEnabled, setDiscountEnabled] = useState(Number(draft.discount_percentage) > 0);
  const [openItemSelect, setOpenItemSelect] = useState(null);
  const readableClass = editable ? "" : "select-text text-foreground";
  const itemModels = cqItemModels(items);

  const updateLine = (collection, key, fieldName, value) => setDraft((current) => ({
    ...current,
    [collection]: current[collection].map((row) => (
      row._key === key ? { ...row, [fieldName]: value } : row
    )),
  }));
  const removeLine = (collection, key) => setDraft((current) => ({
    ...current,
    [collection]: current[collection].filter((row) => row._key !== key),
  }));
  const updatePartItem = (key, itemId) => setDraft((current) => ({
    ...current,
    parts: current.parts.map((row) => {
      if (row._key !== key || row.item_id === itemId) return row;
      const item = items.find((candidate) => candidate.id === itemId);
      return {
        ...row,
        item_id: itemId,
        ...(item ? {
          part_number: item.part_number || item.sku || item.name || "",
          part_name: item.part_name || item.name || "Item",
          unit_price: String(item.price ?? "0"),
        } : {}),
      };
    }),
  }));
  const updatePartModel = (key, model) => setDraft((current) => ({
    ...current,
    parts: current.parts.map((row) => row._key === key ? {
      ...row,
      _model: model,
      _changing_item: true,
      item_id: "",
      part_number: "",
      part_name: "",
      unit_price: "0",
    } : row),
  }));
  const beginPartChange = (key) => setDraft((current) => ({
    ...current,
    parts: current.parts.map((row) => row._key === key ? {
      ...row,
      _model: "",
      _changing_item: true,
      item_id: "",
      part_number: "",
      part_name: "",
      unit_price: "0",
    } : row),
  }));
  const removeSerial = (index) => setDraft((current) => ({
    ...current,
    serial_numbers: current.serial_numbers.filter((_serial, serialIndex) => serialIndex !== index),
  }));
  const addSerial = () => {
    const serial = serialInput.trim();
    if (!serial) return;
    if (draft.serial_numbers.some((value) => value.toLowerCase() === serial.toLowerCase())) {
      setSerialError("That Serial Number has already been added.");
      return;
    }
    setDraft((current) => ({ ...current, serial_numbers: [...current.serial_numbers, serial] }));
    setSerialInput("");
    setSerialError("");
  };
  const amount = (row) => {
    const preview = totals?.line_totals?.[row._key || row.id];
    if (preview != null) return preview;
    try {
      return calculateQuotationLine(row.unit_price, Number(row.quantity)).inclusive_line_total;
    } catch (_error) {
      return null;
    }
  };
  const rows = [
    ...draft.parts.map((row) => ({ ...row, _type: "Spare Part", _collection: "parts" })),
    ...draft.custom_costs.map((row) => ({ ...row, _type: "Custom Cost", _collection: "custom_costs" })),
  ];

  return (
    <section className="min-w-0 rounded-lg border bg-card shadow-brutal" data-testid="cq-standalone-section">
      <div className="border-b">
        <QuotationSectionOneHeader helper={headerHelper} actions={headerAction} />
      </div>
      <div className="min-w-0 space-y-5 p-4">
        <div>
          <div className="text-xs font-medium">Serial Numbers</div>
          <div className="mt-1 flex min-h-11 flex-wrap items-center gap-2 rounded-lg border px-2 py-1.5" data-testid="cq-serial-chips">
            {draft.serial_numbers.map((serial, index) => (
              <span key={`${serial}-${index}`} className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-sm" data-testid="cq-serial-chip">
                {serial}
                {editable ? <button type="button" aria-label={`Remove Serial Number ${serial}`} onClick={() => removeSerial(index)}><X className="h-3.5 w-3.5" /></button> : null}
              </span>
            ))}
            {editable ? (
              <input
                className="min-w-56 flex-1 border-0 bg-transparent p-1 text-sm outline-none"
                value={serialInput}
                placeholder="Type a Serial Number and press Enter"
                aria-label="New Serial Number"
                onChange={(event) => { setSerialInput(event.target.value); setSerialError(""); }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addSerial();
                  } else if (event.key === "Backspace" && !serialInput && draft.serial_numbers.length) {
                    removeSerial(draft.serial_numbers.length - 1);
                  }
                }}
              />
            ) : null}
          </div>
          {serialError ? <p className="mt-1 text-xs text-destructive" role="alert">{serialError}</p> : null}
        </div>

        <div className="grid min-w-0 gap-4 md:grid-cols-2">
          <Field label="Problem / Complaint">
            <textarea className={`${textareaClass} mt-1 ${readableClass}`} readOnly={!editable} value={draft.problem} onChange={(event) => setDraft((current) => ({ ...current, problem: event.target.value }))} />
          </Field>
          <Field label="Resolution">
            <textarea className={`${textareaClass} mt-1 ${readableClass}`} readOnly={!editable} value={draft.resolution} onChange={(event) => setDraft((current) => ({ ...current, resolution: event.target.value }))} />
          </Field>
        </div>

        <section className="min-w-0 space-y-5 border-t pt-4" data-testid="cq-item-ledger">
          <div className="overflow-x-auto rounded-sm border" data-testid="cq-item-table-scroll">
            <table className="w-full min-w-[760px] table-fixed text-left text-sm">
              <colgroup><col className="w-[120px]" /><col /><col className="w-16" /><col className="w-36" /><col className="w-36" /><col className="w-20" /></colgroup>
              <thead className="bg-muted/40 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground"><tr><th className="px-3 py-2">Type</th><th className="px-3 py-2" data-ledger-column="description">Description</th><th className="px-2 py-2 text-right" data-ledger-column="qty">Qty</th><th className="px-3 py-2 text-right">Unit Price</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-right">Action</th></tr></thead>
              <tbody>
                {rows.length ? rows.map((row) => {
                  const part = row._type === "Spare Part";
                  const selection = part ? cqPartSelectionState(row, items) : null;
                  const unchangedSnapshot = part && selection.usesPersistedSnapshot && row._changing_item !== true;
                  const selectableItems = part ? cqItemsForModel(selection.selectableItems, row._model) : [];
                  return (
                    <tr key={row._key} data-cost-type={part ? "spare-part" : "custom"} className="border-t">
                      <td className="px-3 py-3 font-medium">{row._type}</td>
                      <td className="px-3 py-3">{part ? (editable && !unchangedSnapshot ? <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-2"><QuotationItemSelect value={row._model || ""} onChange={(model) => updatePartModel(row._key, model)} options={itemModels.map((model) => ({ value: model, label: model }))} placeholder="Spare Part Model" testid={`cq-model-${row._key}`} open={openItemSelect === `model-${row._key}`} onOpenChange={(open) => setOpenItemSelect(open ? `model-${row._key}` : null)} /><QuotationItemSelect value={row.item_id} onChange={(itemId) => updatePartItem(row._key, itemId)} options={selectableItems.map((item) => ({ value: item.id, label: `${item.part_number || item.sku || item.name} · ${item.part_name || item.name || "Item"}`, sublabel: item.model }))} placeholder="Part Item" testid={`cq-item-${row._key}`} open={openItemSelect === `item-${row._key}`} onOpenChange={(open) => setOpenItemSelect(open ? `item-${row._key}` : null)} disabled={!row._model} /></div> : <div className="select-text"><div className="font-medium">{row.part_number || "-"} · {row.part_name || "-"}</div>{editable ? <Button type="button" variant="neutral" size="sm" className="mt-2" onClick={() => beginPartChange(row._key)}>Change Item</Button> : null}</div>) : <input className={`${inputClass} ${readableClass}`} readOnly={!editable} aria-label="Custom Cost description" value={row.description} onChange={(event) => updateLine("custom_costs", row._key, "description", event.target.value)} />}</td>
                      <td className="px-2 py-3"><input className={`${inputClass} px-2 text-right ${readableClass}`} readOnly={!editable} type="number" min="1" aria-label={`${row._type} quantity`} value={row.quantity} onChange={(event) => updateLine(row._collection, row._key, "quantity", event.target.value)} /></td>
                      <td className="px-3 py-3"><input className={`${inputClass} text-right ${part && mode === "create" ? "bg-muted/40" : readableClass}`} readOnly={!editable || (part && mode === "create")} inputMode="decimal" aria-label={`${row._type} unit price`} value={row.unit_price} onChange={(event) => { if (!part || mode === "edit") updateLine(row._collection, row._key, "unit_price", event.target.value); }} /></td>
                      <td className="select-text px-3 py-3 text-right font-medium tabular-nums">{amount(row) == null ? "-" : formatQuotationMoney(amount(row))}</td>
                      <td className="px-3 py-3 text-right">{editable ? <Button type="button" variant="transfer-remove" size="row-icon" title={`Remove ${row._type}`} aria-label={`Remove ${row._type}`} onClick={() => removeLine(row._collection, row._key)}><Trash2 /></Button> : <span className="text-muted-foreground">-</span>}</td>
                    </tr>
                  );
                }) : <tr><td colSpan="6" className="px-3 py-8 text-center text-muted-foreground">No quotation items.</td></tr>}
              </tbody>
            </table>
            {editable ? <div className="flex justify-end gap-2 border-t px-3 py-3"><Button type="button" variant="transfer-add" size="sm" onClick={() => setDraft((current) => ({ ...current, parts: [...current.parts, { _key: localKey(), _model: "", _changing_item: true, item_id: "", quantity: "1", unit_price: "0" }] }))}><Plus />Add Part</Button><Button type="button" variant="transfer-add" size="sm" onClick={() => setDraft((current) => ({ ...current, custom_costs: [...current.custom_costs, { _key: localKey(), description: "", quantity: "1", unit_price: "0" }] }))}><Plus />Add Custom Cost</Button></div> : null}
          </div>
          <ServiceCostDiscountControl
            checked={mode === "view" ? Number(draft.discount_percentage) > 0 : discountEnabled}
            percentage={draft.discount_percentage}
            reason={draft.discount_reason}
            editable={editable}
            readableViewValues={mode === "view"}
            testid="cq-discount-control"
            onEnabledChange={(enabled) => {
              setDiscountEnabled(enabled);
              if (!enabled) setDraft((current) => ({ ...current, discount_percentage: "0" }));
            }}
            onPercentageChange={(discount_percentage) => {
              if (Number(discount_percentage) > 0) setDiscountEnabled(true);
              setDraft((current) => ({ ...current, discount_percentage }));
            }}
            onReasonChange={(discount_reason) => setDraft((current) => ({ ...current, discount_reason }))}
          />
          <ServiceCostLinearSummary
            gross={totals?.gross || "0"}
            discountAmount={totals?.discount || "0"}
            final={totals?.final || "0"}
            labels={["Gross", "Discount", "Final"]}
            testid="cq-cost-footer"
          />
        </section>
      </div>
    </section>
  );
}
